import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../utilities/axios";

/**
 * Prefer backend validation messages over Axios generic "Request failed with status code …".
 */
function getLeaveApiErrorMessage(
  err,
  fallback = "Oups !Something went wrong! please try again"
) {
  const response = err?.response;
  if (!response) {
    return err?.message || fallback;
  }
  const { status, data } = response;
  if (status === 413) {
    return "File too large. Compress the image or choose a smaller file and try again.";
  }
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object") {
    const msg = data.message;
    if (Array.isArray(msg) && msg.length) {
      return msg.filter(Boolean).join("; ");
    }
    if (typeof msg === "string" && msg.trim()) {
      return msg.trim();
    }
    if (typeof data.error === "string" && data.error.trim()) {
      return data.error.trim();
    }
    if (
      data.error &&
      typeof data.error === "object" &&
      typeof data.error.message === "string"
    ) {
      return data.error.message;
    }
    if (Array.isArray(data.errors) && data.errors.length) {
      const parts = data.errors
        .map((e) =>
          typeof e === "string" ? e : e?.msg || e?.message || String(e)
        )
        .filter(Boolean);
      if (parts.length) {
        return parts.join("; ");
      }
    }
  }
  return err?.message || fallback;
}

const initialState = {
  AllLeaves: [],
  leaveTotal: [],
  leaveVacation: [],
  leaveEmpPending: [],
  getMyLeaves: [],
  userBalance: 0,
  unreadLeave: {},
  applyForNegotiationStatus: "idle",
  applyForNegotiationError: null,

  getMyLeavesStatus: "idle",
  getMyLeavesError: null,

  leaveEmpPendingStatus: "idle",
  leaveEmpPendingError: null,

  leaveVacationStatus: "idle",
  leaveVacationError: null,

  leaveTotalStatus: "idle",
  leaveTotalError: null,

  addLeaveStatus: "idle",
  addLeaveError: null,

  bulkCreateLeavesStatus: "idle",
  bulkCreateLeavesError: null,

  status: "idle",
  error: null,

  approveRequestStatus: "idle",
  approveRequestSuccess: "",
  approveRequestError: "",

  rejectRequestStatus: "idle",
  rejectRequestSuccess: "",
  rejectRequestError: "",

  revertRequestStatus: "idle",
  revertRequestSuccess: "",
  revertRequestError: "",

  approveNegotiationStatus: "idle",
  approveNegotiationSuccess: "",
  approveNegotiationError: "",

  rejectNegotiationStatus: "idle",
  rejectNegotiationSuccess: "",
  rejectNegotiationError: "",

  fetchUserBalanceStatus: "idle",
  fetchUserBalanceSuccess: "",
  fetchUserBalanceError: "",

  // Paginated leaves data
  paginatedLeaves: [],
  paginatedLeavesStatus: "idle",
  paginatedLeavesError: null,
  leavesPagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  calendarLeaves: [],
  calendarLeavesStatus: "idle",
  calendarLeavesError: null,

  monthlyAggregatedLeaves: [],
  monthlyAggregatedLeavesStatus: "idle",
  monthlyAggregatedLeavesError: null,
  monthlyAggregatedPagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

export const applyForNegotiation = createAsyncThunk(
  "leave/applyForNegotiation",
  async (body, thunkAPI) => {
    const {
      id,
      negotiationStartDate,
      negotiationEndDate,
      negotiationDescription,
    } = body;

    let data;
    try {
      const response = await axios.patch(
        `/leaves/negotiation/${id}
    `,
        {
          negotiationStartDate,
          negotiationEndDate,
          negotiationDescription,
        }
      );
      data = await response.data;

      if (response.status === 200) {
        thunkAPI.dispatch(getAllLeaves());

        return {
          data: data,
          message: "Negotiation request have been submitted!",
        };
      }

      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const acceptNegotiation = createAsyncThunk(
  "leave/acceptNegotiation",
  async (id, thunkAPI) => {
    let data;
    try {
      const response = await axios.patch(`/leaves/accept-negotiation/${id}`);
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getMyLeaves());
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const rejectNegotiation = createAsyncThunk(
  "leave/rejectNegotiation",
  async (id, thunkAPI) => {
    let data;
    try {
      const response = await axios.patch(`/leaves/reject-negotiation/${id}`);

      data = await response.data;

      if (response.status === 200) {
        thunkAPI.dispatch(getMyLeaves());

        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const editLeaveCurrentUser = createAsyncThunk(
  "AllLeaves/editLeaveCurrentUser",
  async (body, thunkAPI) => {
    let data;
    try {
      let response;
      
      // If isFormData is true, body.data is FormData, otherwise it's a plain object
      if (body.isFormData) {
        // Send FormData and let the browser set the multipart boundary.
        // Manually setting Content-Type can omit the boundary and break uploads in some browsers/devices.
        response = await axios.patch(`/leaves/edit/${body.id}`, body.data);
      } else {
        // Send plain object as JSON
        const { description, type, dateStart, dateEnd, keepCertificates } = body.data;
        response = await axios.patch(`/leaves/edit/${body.id}`, {
          description,
          type,
          dateStart,
          dateEnd,
          keepCertificates, // Include certificates to keep if present
        });
      }
      
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getMyLeaves());
        return {
          data: data,
          message: "Your leave request has been updated successfully.",
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const getAllLeaves = createAsyncThunk(
  "leaves/getAllLeaves",
  async () => {
    try {
      const response = await axios.get(`/leaves`);
      return response.data;
    } catch (err) {
      return Promise.reject(err.message ? err.message : err);
    }
  }
);

export const fetchPaginatedLeaves = createAsyncThunk(
  "leaves/fetchPaginatedLeaves",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("/leaves/paginated", {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search || "",
          status: params.status || "",
          department: params.department || "",
          type: params.type || "",
          dateStartFrom: params.dateStartFrom ? params.dateStartFrom.toISOString() : "",
          dateStartTo: params.dateStartTo ? params.dateStartTo.toISOString() : "",
          dateEndFrom: params.dateEndFrom ? params.dateEndFrom.toISOString() : "",
          dateEndTo: params.dateEndTo ? params.dateEndTo.toISOString() : "",
          userId: params.userId || "",
          headquarterId: params.headquarterId || "",
          month: params.month || "",
          year: params.year || ""
        }
      });

      if (response.status === 200) {
        return {
          data: response.data.data,
          pagination: response.data.pagination
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Failed to fetch leaves");
    }
  }
);

export const fetchMonthlyAggregatedLeaves = createAsyncThunk(
  "leaves/fetchMonthlyAggregatedLeaves",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("/leaves/monthly-aggregated", {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search || "",
          status: params.status || "",
          department: params.department || "",
          type: params.type || "",
          userId: params.userId || "",
          headquarterId: params.headquarterId || "",
          month: params.month || "",
          year: params.year || "",
        },
      });

      if (response.status === 200) {
        return {
          data: response.data.data,
          pagination: response.data.pagination,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch aggregated leaves"
      );
    }
  }
);

export const fetchLeavesCalendar = createAsyncThunk(
  "leaves/fetchLeavesCalendar",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.get("/leaves/calendar", {
        params: {
          search: params?.search || "",
          status: params?.status || "",
          department: params?.department || "",
          type: params?.type || "",
          dateStartFrom: params?.dateStartFrom ? params.dateStartFrom.toISOString?.() || params.dateStartFrom : "",
          dateStartTo: params?.dateStartTo ? params.dateStartTo.toISOString?.() || params.dateStartTo : "",
          dateEndFrom: params?.dateEndFrom ? params.dateEndFrom.toISOString?.() || params.dateEndFrom : "",
          dateEndTo: params?.dateEndTo ? params.dateEndTo.toISOString?.() || params.dateEndTo : "",
          viewStart: params?.viewStart ? params.viewStart.toISOString?.() || params.viewStart : "",
          viewEnd: params?.viewEnd ? params.viewEnd.toISOString?.() || params.viewEnd : "",
          headquarterId: params?.headquarterId || ""
        }
      });

      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Failed to fetch calendar leaves");
    }
  }
);

export const getMyLeaves = createAsyncThunk(
  "leaveTotal/getMyLeaves",
  async () => {
    let data;
    try {
      const response = await axios.get(`/leaves/my-leaves`);
      data = await response.data;

      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const addLeave = createAsyncThunk(
  "leaveTotal/addLeave",
  async (body, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(`/leaves`, body);

      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getMyLeaves());
        return {
          data: data,
          message: "Your leave request is successfully submited ! ",
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(getLeaveApiErrorMessage(err));
    }
  }
);

export const bulkCreateLeaves = createAsyncThunk(
  "leave/bulkCreateLeaves",
  async (body, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(`/leaves/bulk-create`, body);
      data = await response.data;
      if (response.status === 200) {
        // Refresh leaves list and calendar
        thunkAPI.dispatch(fetchPaginatedLeaves({}));
        thunkAPI.dispatch(fetchLeavesCalendar({}));
        return {
          data: data,
          message: data.message || "Leaves created successfully",
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.response?.data?.message || err.message || "Failed to create bulk leaves"
      );
    }
  }
);

export const fetchAllLeave = createAsyncThunk(
  "leaves/fetchAllLeave",
  async () => {
    let data;
    try {
      const response = await axios.get(`/leaves/requests`);
      data = await response.data;
      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const fetchEmpInVacation = createAsyncThunk(
  "leaves/fetchEmpInVacation",
  async () => {
    let data;
    try {
      const response = await axios.get(`/leaves/employees-vacation`);
      data = await response.data;
      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const fetchEmppending = createAsyncThunk(
  "leaves/fetchEmppending",
  async () => {
    let data;
    try {
      const response = await axios.get(`/leaves/pending`);
      data = await response.data;

      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const approveLeaveRequest = createAsyncThunk(
  "leave/approveLeaveRequest",
  async (id, thunkAPI) => {
    let data;
    try {
      const response = await axios.patch(`/leaves/approve/${id}
    `);
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getAllLeaves());
        return data.message;
      }

      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const rejectLeaveRequest = createAsyncThunk(
  "leave/rejectLeaveRequest",
  async (data, thunkAPI) => {
    const { id, descriptionReject } = data;

    try {
      const response = await axios.patch(
        `/leaves/reject/${id}
    `,

        { descriptionReject: descriptionReject }
      );
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getAllLeaves());

        return data.message;
      }

      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);
export const negotiateLeaveRequest = createAsyncThunk(
  "leave/negotiateLeaveRequest",
  async (body, thunkAPI) => {
    const { id, startDate, endDate, description } = body;

    let data;
    try {
      const response = await axios.patch(
        `/leaves/negotiation/${id}
    `,
        { startDate: startDate, endDate: endDate, description: description }
      );
      data = await response.data;

      if (response.status === 200) {
        thunkAPI.dispatch(getAllLeaves());
        return data.message;
      }

      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const fetchUserBalance = createAsyncThunk(
  "leave/fetchUserBalance",
  async (id) => {
    let data;
    try {
      const response = await axios.get(`/leaves/balance/${id}`);
      data = await response.data;
      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const deleteLeave = createAsyncThunk(
  "leave/deleteLeave",
  async (id, thunkAPI) => {
    let data;
    try {
      const response = await axios.patch(`/leaves/delete/${id}`);
      data = await response.data;
      if (response.status === 200) {
        const state = thunkAPI.getState();
        const { code } = state.user.fetchMe?.role;
        code === "SUPER ADMIN" ?
           thunkAPI.dispatch(getAllLeaves())
         : 
          thunkAPI.dispatch(getMyLeaves());
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const revertLeaveRequest = createAsyncThunk(
  "leave/revertLeaveRequest",
  async (data, thunkAPI) => {
    const { id, reason } = data;
    let responseData;
    try {
      const response = await axios.patch(`/leaves/revert/${id}`, {
        reason: reason || undefined
      });
      responseData = await response.data;
      if (response.status === 200) {
        // Refresh paginated leaves and calendar
        const state = thunkAPI.getState();
        const currentParams = state.leave.leavesPagination;
        thunkAPI.dispatch(fetchPaginatedLeaves({
          page: currentParams.page,
          limit: currentParams.limit,
          ...state.leave.filters
        }));
        thunkAPI.dispatch(fetchLeavesCalendar({}));
        return responseData.message || "Leave reverted successfully";
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.response?.data?.message || err.message || "Failed to revert leave"
      );
    }
  }
);

const slice = createSlice({
  name: "leave",
  initialState,
  reducers: {
    handleNewLeave: (state, action) => {
      state.AllLeaves.push(action.payload.newLeave);
    },
  },
  extraReducers: {
    [getAllLeaves.pending]: (state) => {
      state.status = "loading";
    },
    [getAllLeaves.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.AllLeaves = action.payload;
    },
    [getAllLeaves.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },

    [getMyLeaves.pending]: (state) => {
      state.getMyLeavesStatus = "loading";
    },
    [getMyLeaves.fulfilled]: (state, action) => {
      state.getMyLeavesStatus = "succeeded";
      
      // Handle different response structures
      let leavesData = [];
      if (Array.isArray(action.payload)) {
        // Direct array response
        leavesData = action.payload;
      } else if (action.payload && action.payload.leaves && Array.isArray(action.payload.leaves)) {
        // Object with leaves property
        leavesData = action.payload.leaves;
      } else {
        leavesData = [];
      }
      
      state.getMyLeaves = leavesData;
    },
    [getMyLeaves.rejected]: (state, action) => {
      state.getMyLeavesStatus = "failed";
      state.getMyLeavesError = action.payload;
    },

    [fetchAllLeave.pending]: (state) => {
      state.leaveTotalStatus = "loading";
    },
    [fetchAllLeave.fulfilled]: (state, action) => {
      state.leaveTotalStatus = "leaveTotalStatus";
      state.leaveTotal = action.payload;
    },
    [fetchAllLeave.rejected]: (state, action) => {
      state.leaveTotalStatus = "failed";
      state.leaveTotalError = action.payload;
    },

    [fetchEmpInVacation.pending]: (state) => {
      state.leaveVacationStatus = "loading";
    },

    [fetchEmpInVacation.fulfilled]: (state, action) => {
      state.leaveVacationStatus = "succeeded";
      state.leaveVacation = action?.payload;
    },

    [fetchEmpInVacation.rejected]: (state, action) => {
      state.leaveVacationStatus = "failed";
      state.leaveVacationError = action.error;
    },

    [fetchEmppending.pending]: (state) => {
      state.leaveEmpPendingStatus = "loading";
    },

    [fetchEmppending.fulfilled]: (state, action) => {
      state.leaveEmpPendingStatus = "succeeded";
      state.leaveEmpPending = action.payload;
    },

    [fetchEmppending.rejected]: (state, action) => {
      state.leaveEmpPendingError = "failed";
      state.error = action.payload;
    },
    [approveLeaveRequest.pending]: (state) => {
      state.approveRequestStatus = "loading";
    },

    [approveLeaveRequest.fulfilled]: (state, action) => {
      state.approveRequestStatus = "succeeded";
      state.approveRequestSuccess = action.payload;
    },

    [approveLeaveRequest.rejected]: (state, action) => {
      state.approveRequestStatus = "failed";
      state.approveRequestError = action.payload;
    },
    [rejectLeaveRequest.pending]: (state) => {
      state.rejectRequestStatus = "loading";
    },

    [rejectLeaveRequest.fulfilled]: (state, action) => {
      state.rejectRequestStatus = "succeeded";
      state.rejectRequestSuccess = action.payload;
    },

    [rejectLeaveRequest.rejected]: (state, action) => {
      state.rejectRequestStatus = "failed";
      state.rejectRequestError = action.payload;
    },
    [revertLeaveRequest.pending]: (state) => {
      state.revertRequestStatus = "loading";
    },
    [revertLeaveRequest.fulfilled]: (state, action) => {
      state.revertRequestStatus = "succeeded";
      state.revertRequestSuccess = action.payload;
    },
    [revertLeaveRequest.rejected]: (state, action) => {
      state.revertRequestStatus = "failed";
      state.revertRequestError = action.payload;
    },
    [acceptNegotiation.pending]: (state) => {
      state.approveNegotiationStatus = "loading";
    },

    [acceptNegotiation.fulfilled]: (state, action) => {
      state.approveNegotiationStatus = "succeeded";
      state.approveNegotiationSuccess = action.payload;
    },

    [acceptNegotiation.rejected]: (state, action) => {
      state.approveNegotiationStatus = "failed";
      state.approveNegotiationError = action.error;
    },
    [rejectNegotiation.pending]: (state) => {
      state.rejectNegotiationStatus = "loading";
    },

    [rejectNegotiation.fulfilled]: (state, action) => {
      state.rejectNegotiationStatus = "succeeded";
      state.rejectNegotiationSuccess = action.payload;
    },

    [rejectNegotiation.rejected]: (state, action) => {
      state.rejectNegotiationStatus = "failed";
      state.rejectNegotiationError = action.error;
    },

    [addLeave.pending]: (state) => {
      state.addLeaveStatus = "loading";
    },
    [addLeave.fulfilled]: (state, action) => {
      state.addLeaveStatus = "succeeded";
    },
    [addLeave.rejected]: (state, action) => {
      state.addLeaveStatus = "failed";
      state.addLeaveError =
        action.meta?.rejectedWithValue && action.payload != null
          ? { message: String(action.payload) }
          : action.error;
    },

    [applyForNegotiation.pending]: (state) => {
      state.applyForNegotiationStatus = "loading";
    },
    [applyForNegotiation.fulfilled]: (state) => {
      state.applyForNegotiationStatus = "succeeded";
    },
    [applyForNegotiation.rejected]: (state, action) => {
      state.applyForNegotiationStatus = "failed";
      state.applyForNegotiationError = action.error;
    },
    [fetchUserBalance.pending]: (state) => {
      state.fetchUserBalanceStatus = "loading";
    },
    [fetchUserBalance.fulfilled]: (state, action) => {
      state.fetchUserBalanceStatus = "succeeded";
      state.userBalance = action.payload.balance;
    },
    [fetchUserBalance.rejected]: (state, action) => {
      state.fetchUserBalanceStatus = "failed";
      state.fetchUserBalanceError = action.error;
    },

    [fetchPaginatedLeaves.pending]: (state) => {
      state.paginatedLeavesStatus = "loading";
      state.paginatedLeavesError = null;
    },
    [fetchPaginatedLeaves.fulfilled]: (state, action) => {
      state.paginatedLeavesStatus = "succeeded";
      state.paginatedLeaves = action.payload.data;
      state.leavesPagination = action.payload.pagination;
      state.paginatedLeavesError = null;
    },
    [fetchPaginatedLeaves.rejected]: (state, action) => {
      state.paginatedLeavesStatus = "failed";
      state.paginatedLeavesError = action.payload;
    },
    [fetchMonthlyAggregatedLeaves.pending]: (state) => {
      state.monthlyAggregatedLeavesStatus = "loading";
      state.monthlyAggregatedLeavesError = null;
    },
    [fetchMonthlyAggregatedLeaves.fulfilled]: (state, action) => {
      state.monthlyAggregatedLeavesStatus = "succeeded";
      state.monthlyAggregatedLeaves = action.payload.data;
      state.monthlyAggregatedPagination = action.payload.pagination;
      state.monthlyAggregatedLeavesError = null;
    },
    [fetchMonthlyAggregatedLeaves.rejected]: (state, action) => {
      state.monthlyAggregatedLeavesStatus = "failed";
      state.monthlyAggregatedLeavesError = action.payload;
    },
    [fetchLeavesCalendar.pending]: (state) => {
      state.calendarLeavesStatus = "loading";
      state.calendarLeavesError = null;
    },
    [fetchLeavesCalendar.fulfilled]: (state, action) => {
      state.calendarLeavesStatus = "succeeded";
      state.calendarLeaves = action.payload || [];
    },
    [fetchLeavesCalendar.rejected]: (state, action) => {
      state.calendarLeavesStatus = "failed";
      state.calendarLeavesError = action.payload;
    },

    [bulkCreateLeaves.pending]: (state) => {
      state.bulkCreateLeavesStatus = "loading";
      state.bulkCreateLeavesError = null;
    },
    [bulkCreateLeaves.fulfilled]: (state, action) => {
      state.bulkCreateLeavesStatus = "succeeded";
    },
    [bulkCreateLeaves.rejected]: (state, action) => {
      state.bulkCreateLeavesStatus = "failed";
      state.bulkCreateLeavesError = action.payload;
    },
  },
});
export const { handleNewLeave } = slice.actions;

export const reducer = slice.reducer;

export default slice;